<template>
    <div class="step-subcription-root">
        <h1>{{ $l10n.__('Abonnement de') }} {{ customer.name }}</h1>

        <div v-if="isCartLoaded">
            <el-alert
                v-if="savedCartStatus == 'draft'"
                :closable="false"
                :title="
                    $t.__(
                        'sellsyaccountmanager.subscriptionform.SubscriptionSale.cart_edited_in_draft_by_sale',
                    )
                "
                type="error"
            >
            </el-alert>
            <el-alert
                v-else
                :closable="false"
                :title="
                    $t.__(
                        'sellsyaccountmanager.subscriptionform.SubscriptionSale.active_cart_already_exist',
                    )
                "
                type="error"
            >
            </el-alert>
        </div>

        <el-row :gutter="40">
            <el-col :span="12">
                <!-- CONTACT -->
                <div class="mb-4">
                    <h4>{{ $filters.capitalize($l10n.__('contact')) }}</h4>

                    <el-form
                        ref="billingContactForm"
                        :inlineMessage="true"
                        :model="billingContactTmp"
                        :rules="samBillingContactRules"
                        class="well pb-0"
                        labelPosition="top"
                    >
                        <el-form-item
                            :error="errors.get('contactFirstName')"
                            :label="$t.__('common.labels.first_name')"
                            prop="contactFirstName"
                        >
                            <TextInput v-model="billingContactTmp.contactFirstName" mode="edit" />
                        </el-form-item>

                        <el-form-item
                            :error="errors.get('contactName')"
                            :label="$t.__('common.labels.name')"
                            prop="contactName"
                        >
                            <TextInput v-model="billingContactTmp.contactName" mode="edit" />
                        </el-form-item>

                        <el-form-item
                            :error="errors.get('contactEmail')"
                            :label="$t.__('common.labels.email_address')"
                            prop="contactEmail"
                        >
                            <TextInput
                                v-model="billingContactTmp.contactEmail"
                                mode="edit"
                                type="email"
                            />
                        </el-form-item>

                        <el-form-item
                            :error="errors.get('contactPhone')"
                            :label="$t.__('common.labels.phone')"
                            prop="contactPhone"
                        >
                            <tel-input
                                v-model="billingContactPhoneTmp"
                                mode="edit"
                                @change="updateBilligContactPhoneTmp"
                            />
                        </el-form-item>

                        <el-form-item
                            :error="errors.get('locale')"
                            :label="$t.__('common.labels.invoicing_language')"
                            prop="locale"
                        >
                            <SelectInput
                                v-model="billingContactTmp.locale"
                                :filterable="true"
                                :options="billingLocales"
                                class="m-0"
                                idColumn="code"
                                labelColumn="name"
                                mode="edit"
                            />
                        </el-form-item>

                        <el-form-item
                            :error="errors.get('additionalEmailRecipients')"
                            :label="$t.__('common.labels.offer_recipient_emails')"
                            :rules="additionalEmailRecipientsRules"
                            prop="additionalEmailRecipients"
                        >
                            <el-select
                                v-model="billingContactTmp.additionalEmailRecipients"
                                :collapseTags="false"
                                :placeholder="$t.__('common.actions.search_email')"
                                allowCreate
                                class="wp-10 no-caret"
                                defaultFirstOption
                                filterable
                                multiple
                                popperClass="hidden"
                            >
                            </el-select>
                        </el-form-item>
                    </el-form>
                </div>

                <!--  ADDRESS -->
                <div class="mb-4">
                    <h4>{{ $filters.capitalize($l10n.__('adresse de facturation du client')) }}</h4>
                    <el-form
                        ref="billingAddressForm"
                        :inlineMessage="true"
                        :model="billingAddressTmp"
                        :rules="samBillingAddressAndIntraVatRules"
                        class="well pb-0"
                        labelPosition="top"
                    >
                        <el-form-item
                            :error="errors.get('line1')"
                            :label="$t.__('common.labels.street_name')"
                            prop="line1"
                        >
                            <algolia-places
                                :defaultValue="billingAddressTmp.line1"
                                :templates="{
                                    value: (suggestion) => {
                                        return suggestion.name;
                                    },
                                }"
                                @bubble-address="captureAddress"
                            ></algolia-places>
                        </el-form-item>

                        <el-form-item
                            :error="errors.get('line2')"
                            :label="$t.__('common.labels.more_address_informations')"
                            prop="line2"
                        >
                            <TextInput v-model="billingAddressTmp.line2" mode="edit" />
                        </el-form-item>

                        <div class="flex-level">
                            <div class="flex pr-2">
                                <el-form-item
                                    :error="errors.get('postalCode')"
                                    :label="$t.__('common.labels.postal_code')"
                                    prop="postalCode"
                                >
                                    <TextInput v-model="billingAddressTmp.postalCode" mode="edit" />
                                </el-form-item>
                            </div>
                            <div class="flex-3 pl-2">
                                <el-form-item
                                    :error="errors.get('city')"
                                    :label="$t.__('common.labels.city')"
                                    prop="city"
                                >
                                    <TextInput v-model="billingAddressTmp.city" mode="edit" />
                                </el-form-item>
                            </div>
                        </div>

                        <div class="flex-level">
                            <div class="flex pr-2">
                                <el-form-item
                                    :error="errors.get('state')"
                                    :label="$t.__('common.labels.state')"
                                    prop="state"
                                >
                                    <TextInput
                                        v-model="billingAddressTmp.state"
                                        class="m-0"
                                        mode="edit"
                                    />
                                </el-form-item>
                            </div>
                            <div class="flex pl-2">
                                <el-form-item
                                    :error="errors.get('country')"
                                    :label="$t.__('common.labels.country')"
                                    prop="country"
                                >
                                    <SelectInput
                                        v-model="billingAddressTmp.country"
                                        :filterable="true"
                                        :options="countries"
                                        class="m-0"
                                        idColumn="code"
                                        labelColumn="name"
                                        mode="edit"
                                    />
                                </el-form-item>
                            </div>
                        </div>

                        <!-- LEGAL INFOS -->
                        <div v-if="showTaxTotalInForm" class="mt-4 container-tiny m-auto">
                            <el-form-item
                                :error="errors.get('vatIdentificationNumber')"
                                :label="$t.__('common.labels.tax_number')"
                                prop="vatIdentificationNumber"
                            >
                                <TextInput
                                    v-model="billingAddressTmp.vatIdentificationNumber"
                                    mode="edit"
                                />
                            </el-form-item>
                        </div>
                    </el-form>
                </div>
                <!-- Custom Message Mail -->
                <div class="mb-4">
                    <h4>{{ $l10n.__('Personnalisation des emails') }}</h4>
                    <el-form
                        ref="billingCustomMail"
                        :inlineMessage="true"
                        :model="billingCustomMail"
                        :rules="samBillingCustomMailRules"
                        class="well pb-0"
                        labelPosition="top"
                    >
                        <el-form-item>
                            <template #label>
                                <span class="semibold">{{ $l10n.__('Message personnalisé') }}</span>
                            </template>
                            <el-checkbox v-model="activeCustomMail" @change="handleCustomMail"
                                >{{
                                    $l10n.__('Joindre mon message personnalisé dans l’email envoyé')
                                }}
                            </el-checkbox>
                        </el-form-item>
                        <el-form-item v-if="activeCustomMail">
                            <RichText
                                v-model="billingCustomMail.message"
                                :options="optionsRichText"
                                :placeholder="$t.__('common.actions.enter_custom_message')"
                                class="custom-richtext"
                            />
                        </el-form-item>
                        <el-form-item>
                            <template #label>
                                <span class="semibold">{{ $l10n.__('Infos de contact') }}</span>
                            </template>
                            <el-checkbox v-model="billingCustomMail.contactAdded"
                                >{{ $l10n.__('Ajouter mes informations de contact') }}
                            </el-checkbox>
                        </el-form-item>
                        <el-form-item
                            v-if="billingCustomMail.contactAdded"
                            :label="
                                $t.__(
                                    'sellsyaccountmanager.subscriptionform.SubscriptionSale.name_lastname',
                                )
                            "
                            prop="contactName"
                        >
                            <el-input v-model="billingCustomMail.contactName"></el-input>
                        </el-form-item>
                        <div v-if="billingCustomMail.contactAdded" class="flex-row">
                            <el-form-item
                                :label="$t.__('common.labels.email')"
                                class="wp-5"
                                prop="contactMailAddress"
                            >
                                <el-input v-model="billingCustomMail.contactMailAddress"></el-input>
                            </el-form-item>
                            <el-form-item
                                :label="
                                    $t.__(
                                        'sellsyaccountmanager.subscriptionform.SubscriptionSale.phone_number',
                                    )
                                "
                                class="wp-5 ml-3"
                                prop="contactPhone"
                            >
                                <tel-input
                                    v-model="billingCustomMailContactPhone"
                                    @change="updateCustomMailContactPhone"
                                />
                            </el-form-item>
                        </div>
                    </el-form>
                </div>
            </el-col>
            <el-col :span="12">
                <!-- CONF -->
                <h4>{{ $l10n.__('Configuration compte') }}</h4>

                <div class="well py-1">
                    <!-- EXPIRED DATE -->
                    <div class="h-5 flex-level">
                        <span class="flex">{{ $l10n.__("Expiration de l'offre") }}</span>
                        <DateInput
                            :modelValue="expireDate"
                            :canBeInThePast="false"
                            :placeholder="$t.__('common.labels.expire_date')"
                            class="w-2"
                            mode="edit"
                            @update:modelValue="expirationDateUpdated"
                        />
                    </div>

                    <!-- CART CONTEXT -->
                    <div class="h-5 flex-level bt-1">
                        <span class="flex">{{ $l10n.__('Contexte') }}</span>
                        <SelectInput
                            :options="saleContexts"
                            :placeholder="
                                $t.__(
                                    'sellsyaccountmanager.subscriptionform.SubscriptionSale.select_context',
                                )
                            "
                            :modelValue="cartContext"
                            class="w-2 aright flex-right"
                            idColumn="value"
                            labelColumn="label"
                            mode="edit"
                            @update:modelValue="contextUpdated"
                        />
                    </div>

                    <!-- PERIOD SERVICE START -->
                    <div v-if="!isCartContext('upsale')" class="h-5 flex-level bt-1">
                        <span v-if="isCartContext('new')" class="flex">{{
                            $l10n.__("Date d'activation")
                        }}</span>
                        <span v-else class="flex">{{ $l10n.__('Date de renouvellement') }}</span>
                        <span v-if="isCartContext('new')" class="mx-2">{{
                            $l10n.__('à partir du')
                        }}</span>
                        <DateInput
                            :modelValue="startDate"
                            :canBeInThePast="false"
                            :disabled="!isCartContext('new')"
                            :placeholder="$t.__('common.labels.start_date')"
                            mode="edit"
                            @update:modelValue="periodApplianceStartUpdated"
                        />
                    </div>

                    <!-- EARLY RENEWAL  -->
                    <div v-if="canEarlyRenew" class="h-5 flex-level bt-1">
                        <span class="flex">{{ $l10n.__('Renouvellement anticipé') }}</span>
                        <input :checked="cartEarlyRenewal" type="checkbox" @change="setEarly" />
                    </div>
                    <div v-else-if="cartEarlyRenewal" class="h-5 flex-level bt-1">
                        <span class="flex"></span>
                        <span>{{ $l10n.__('Renouvellement anticipé') }}</span>
                    </div>

                    <!-- PERIOD SERVICE START -->
                    <div
                        v-if="!isCartContext('new') && customerServiceStartDate"
                        class="h-5 flex-level bt-1"
                    >
                        <span class="flex">{{ $l10n.__('Actuellement engagé') }}</span>
                        <span>{{
                            $l10n.__('du %(start)s au %(end)s', {
                                start: $filters.dateFormatter(
                                    customerServiceStartDateObj.unix(),
                                    'NORMAL_DATE',
                                    false,
                                ),
                                end: $filters.dateFormatter(
                                    customerServiceEndDateObj.unix(),
                                    'NORMAL_DATE',
                                    false,
                                ),
                            })
                        }}</span>
                    </div>
                </div>

                <!-- CART -->
                <h4>{{ $l10n.__('Panier Client') }}</h4>

                <el-form
                    ref="subscriptionForm"
                    :inlineMessage="true"
                    :model="subscription"
                    class="well"
                    labelPosition="top"
                >
                    <!-- PRICING -->
                    <el-form-item
                        :error="errors.get('pricingPlan')"
                        class="pb-3 m-0 bb-1"
                        prop="pricingPlan"
                    >
                        <h4 class="mt-2 dinlineb">{{ $l10n.__('Type de paiement') }}</h4>
                        <RadioInput
                            :choices="pricingPlansChoices"
                            :disabled="isCartContext('upsale')"
                            :modelValue="currentPricingPlan"
                            class="dinlineb fright"
                            mode="edit"
                            radioStyle="button"
                            @change="
                                [
                                    switchAllStepsPricing($event),
                                    switchCartItemPricing($event),
                                    switchPricingPlan($event),
                                ]
                            "
                        />
                    </el-form-item>

                    <!-- USERS -->
                    <el-form-item
                        v-if="cartBasicProduct"
                        :error="errors.get('nbLicences')"
                        :rules="minLicenceValidator"
                        class="bb-1 pb-3 mb-1"
                        prop="nbLicences"
                    >
                        <div>
                            <h4>{{ $l10n.__('Utilisateurs et Outils') }}</h4>

                            <template v-for="(pricingStep, index) of userPricingSteps">
                                <!-- Pricing Users -->
                                <div
                                    v-if="index !== 0"
                                    :key="index"
                                    class="flex-level ai-center mt-1 pricing-step"
                                >
                                    <!-- Row Label -->
                                    <span
                                        v-if="
                                            index === userPricingSteps.length - 1 &&
                                            userPricingSteps.length > 2
                                        "
                                        >{{
                                            $l10n.n__(
                                                'À partir de %s utilisateur',
                                                'À partir de %s utilisateurs',
                                                userPricingSteps[index - 1].up_to + 1,
                                                userPricingSteps[index - 1].up_to + 1,
                                            )
                                        }}</span
                                    >
                                    <template v-else-if="index !== userPricingSteps.length - 1">
                                        <div class="flex-level">
                                            <span
                                                >{{
                                                    $l10n.n__(
                                                        'De %(base)s à %(upto)s utilisateur',
                                                        'De %(base)s à %(upto)s utilisateurs',
                                                        pricingStep.nbLicences,
                                                        {
                                                            base:
                                                                userPricingSteps[index - 1].up_to +
                                                                1,
                                                            upto:
                                                                pricingStep.nbLicences === 0
                                                                    ? userPricingSteps[index - 1]
                                                                          .up_to + 1
                                                                    : pricingStep.up_to,
                                                        },
                                                    )
                                                }}
                                            </span>
                                        </div>
                                    </template>
                                    <template v-else>
                                        <span>{{ $l10n.__('Utilisateurs') }}</span>
                                    </template>
                                    <!-- Nb licences for this step -->
                                    <el-input-number
                                        :min="getMininalLicence(pricingStep.nbLicences, 1)"
                                        :modelValue="pricingStep.nbLicences"
                                        class="w-100 mx-2 flex-item-right"
                                        controlsPosition="right"
                                        @change="($event) => updateLicences($event, index)"
                                    >
                                    </el-input-number>
                                    <!-- Custom Pricing -->
                                    <SelectInput
                                        v-if="
                                            getProductPlan(pricingBasicProduct, currentPricingPlan)
                                                .isCustomPricingAmountInCentsEnabled
                                        "
                                        v-model="pricingStep.unit_amount"
                                        :options="
                                            getCustomPricingAmountForProduct(
                                                pricingBasicProduct,
                                                currentPricingPlan,
                                            )
                                        "
                                        class="mx-2 dinlineb amount-month-price-select"
                                        idColumn="value"
                                        labelColumn="label"
                                        mode="edit"
                                        @update:modelValue="updatePricingStep"
                                    />
                                    <span v-else class="mx-1">{{
                                        formatAmount(
                                            currentPricingPlan === 'MONTHLY'
                                                ? cartBasicProduct.customPricing
                                                : cartBasicProduct.customPricing / 12,
                                            false,
                                            true,
                                        )
                                    }}</span>
                                    <span
                                        >{{ currencySymbol }} / {{ $l10n.__('mois') }} /
                                        {{ $l10n.__('unité') }}.</span
                                    >

                                    <!-- Delete step -->
                                    <el-button
                                        v-if="userPricingSteps.length > 2"
                                        class="red delete-pricing-step pl-4 pr-2"
                                        type="text"
                                        @click="deletePricingStep(index)"
                                    >
                                        <i class="icon-less"></i>
                                    </el-button>
                                </div>
                            </template>
                            <CompassButton
                                :label="$t.__('common.actions.add_step')"
                                class="mt-2"
                                type="primary"
                                @click="addPricingStep"
                            />

                            <!-- Free Users -->
                            <div class="flex-level flex-right flex mt-4 ai-center">
                                <span class="mr-2">
                                    {{
                                        $l10n.n__(
                                            'Utilisateur offert',
                                            'Utilisateurs offerts',
                                            userPricingSteps[0].up_to,
                                        )
                                    }}
                                </span>
                                <el-input-number
                                    v-model="userPricingSteps[0].nbLicences"
                                    :min="getMininalLicence(userPricingSteps[0].nbLicences, 0)"
                                    class="w-80"
                                    controlsPosition="right"
                                    @change="($event) => updateLicences($event, 0)"
                                ></el-input-number>
                            </div>
                            <div class="flex flex-right flex-level mt-2">
                                {{ $l10n.__("Total du nombre d'utilisateurs") }}
                                <el-input-number
                                    :controls="false"
                                    :modelValue="totalNbLicences"
                                    class="total-user w-80 ml-2"
                                    disabled
                                />
                            </div>
                        </div>
                    </el-form-item>

                    <!-- PRODUCT -->
                    <ul class="m-0 product-list">
                        <li class="bb-1 py-3 flex-level h-5">
                            <h3 class="w-3 m-0">{{ pricingBasicProduct.printableName }}</h3>
                            <span class="flex-2 aright m-0">
                                {{ $filters.capitalize($l10n.__('inclus')) }}
                            </span>
                        </li>
                        <li
                            v-for="(product, index) in pricingOptionalProducts"
                            :key="product.code"
                            class="bb-1 py-3 flex-level h-5"
                        >
                            <h3 class="m-0 product-line-title">{{ product.printableName }}</h3>
                            <div class="flex product-line-basic-price">
                                <span
                                    v-if="
                                        getProductMonthlyAmount(product, currentPricingPlan) !==
                                        getProductMonthlyAmount(product, currentPricingPlan, false)
                                    "
                                    class="block gray-60 fs-3 regular"
                                >
                                    {{
                                        $filters.capitalize(
                                            $l10n.__('tarif de base: %(amount_f)s/mois', {
                                                amount_f: formatAmount(
                                                    getPricingByPlan(
                                                        getProductMonthlyAmount(
                                                            product,
                                                            currentPricingPlan,
                                                            false,
                                                        ),
                                                    ),
                                                ),
                                            }),
                                        )
                                    }}
                                </span>
                            </div>
                            <div class="flex-level aright product-line-price">
                                <SelectInput
                                    v-if="
                                        getProductPlan(product, currentPricingPlan)
                                            .isCustomPricingAmountInCentsEnabled
                                    "
                                    :disabled="
                                        isProductSubscribedByCustomer(product.code) ||
                                        !getCartItem(product.id) ||
                                        offersProducts.includes(index)
                                    "
                                    :options="
                                        getCustomPricingAmountForProduct(
                                            product,
                                            currentPricingPlan,
                                        )
                                    "
                                    :modelValue="
                                        getProductMonthlyAmount(product, currentPricingPlan)
                                    "
                                    idColumn="value"
                                    labelColumn="label"
                                    mode="edit"
                                    @update:modelValue="
                                        updateCartItemCustomPricing(product.id, $event)
                                    "
                                />
                                <strong v-else>{{
                                    formatAmount(
                                        getProductMonthlyAmount(product, currentPricingPlan),
                                        false,
                                        true,
                                    )
                                }}</strong>
                                <span class="ml-1 gray-60 fs-3 regular">
                                    / {{ $filters.capitalize($l10n.__('mois')) }}</span
                                >
                            </div>
                            <div class="flex-level ml-4">
                                <el-checkbox
                                    ref="offer"
                                    :checked="
                                        getProductMonthlyAmount(product, currentPricingPlan) === 0
                                    "
                                    :disabled="!canProductBeToggled(product, false)"
                                    @change="(e) => offerProduct(product, index, e)"
                                    >{{ $l10n.__('Offert') }}
                                </el-checkbox>
                            </div>
                            <div class="ml-4 flex-level aright">
                                <el-switch
                                    :disabled="!canProductBeToggled(product, false)"
                                    :modelValue="!!getCartItem(product.id)"
                                    @change="toggleItem(product)"
                                />
                            </div>
                        </li>

                        <!-- Products with Groups -->
                        <li
                            v-for="(group, index) in pricingOptionalProductsGroups"
                            :key="group"
                            class="bb-1 py-3"
                        >
                            <h3 class="m-0 product-line-title">{{ $filters.capitalize(group) }}</h3>
                            <div class="flex-level">
                                <div class="flex-level flex">
                                    <p class="w-80 flex-level">
                                        <a
                                            :title="
                                                $t.__(
                                                    'sellsyaccountmanager.subscriptionform.SubscriptionSale.rocketchart_plan',
                                                )
                                            "
                                            class="fs-3 gray-80 dinlineb"
                                            href="https://rocketchart.co/pricing/"
                                            target="_blank"
                                        >
                                            <i class="icon-help"></i>
                                        </a>
                                        <span class="ml-1">{{ $l10n.__('Plan') }}</span>
                                    </p>
                                    <el-select
                                        v-model="optionalGroupOptions[index].plan"
                                        :disabled="
                                            isProductSubscribedByCustomer(
                                                getGroupedProductByCode(
                                                    optionalGroupOptions[index].plan,
                                                ).code,
                                            ) ||
                                            !getCartItem(
                                                getGroupedProductByCode(
                                                    optionalGroupOptions[index].plan,
                                                ).id,
                                            ) ||
                                            offersProducts.includes(index)
                                        "
                                        class="ml-2 mr-3"
                                        @change="
                                            toggleItem(
                                                getGroupedProductByCode(
                                                    optionalGroupOptions[index].plan,
                                                ),
                                            )
                                        "
                                    >
                                        <el-option
                                            v-for="product in getProductsGroupable(group)"
                                            :key="product.id"
                                            :label="product.printableName.split('-')[1].trim()"
                                            :value="product.code"
                                        >
                                        </el-option>
                                    </el-select>
                                </div>
                                <template v-if="optionalGroupOptions[index].plan">
                                    <div class="flex-level aright product-line-price">
                                        <SelectInput
                                            v-if="
                                                getProductPlan(
                                                    getGroupedProductByCode(
                                                        optionalGroupOptions[index].plan,
                                                    ),
                                                    currentPricingPlan,
                                                ).isCustomPricingAmountInCentsEnabled
                                            "
                                            :disabled="
                                                isProductSubscribedByCustomer(
                                                    getGroupedProductByCode(
                                                        optionalGroupOptions[index].plan,
                                                    ).code,
                                                ) ||
                                                !getCartItem(
                                                    getGroupedProductByCode(
                                                        optionalGroupOptions[index].plan,
                                                    ).id,
                                                ) ||
                                                offersProducts.includes(index)
                                            "
                                            :options="
                                                getCustomPricingAmountForProduct(
                                                    getGroupedProductByCode(
                                                        optionalGroupOptions[index].plan,
                                                    ),
                                                    currentPricingPlan,
                                                )
                                            "
                                            :modelValue="
                                                getProductMonthlyAmount(
                                                    getGroupedProductByCode(
                                                        optionalGroupOptions[index].plan,
                                                    ),
                                                    currentPricingPlan,
                                                )
                                            "
                                            idColumn="value"
                                            labelColumn="label"
                                            mode="edit"
                                            @update:modelValue="
                                                updateCartItemCustomPricing(
                                                    getGroupedProductByCode(
                                                        optionalGroupOptions[index].plan,
                                                    ).id,
                                                    $event,
                                                )
                                            "
                                        />
                                        <strong v-else>{{
                                            formatAmount(
                                                getProductMonthlyAmount(
                                                    getGroupedProductByCode(
                                                        optionalGroupOptions[index].plan,
                                                    ),
                                                    currentPricingPlan,
                                                ),
                                                false,
                                                true,
                                            )
                                        }}</strong>
                                        <span class="ml-1 gray-60 fs-3 regular">
                                            / {{ $filters.capitalize($l10n.__('mois')) }}</span
                                        >
                                    </div>
                                    <div class="flex-level ml-4">
                                        <el-checkbox
                                            ref="offer"
                                            :checked="
                                                getProductMonthlyAmount(
                                                    getGroupedProductByCode(
                                                        optionalGroupOptions[index].plan,
                                                    ),
                                                    currentPricingPlan,
                                                ) === 0
                                            "
                                            :disabled="
                                                !canProductBeToggled(
                                                    getGroupedProductByCode(
                                                        optionalGroupOptions[index].plan,
                                                    ),
                                                    false,
                                                )
                                            "
                                            @change="
                                                (e) =>
                                                    offerProduct(
                                                        getGroupedProductByCode(
                                                            optionalGroupOptions[index].plan,
                                                        ),
                                                        index,
                                                        e,
                                                    )
                                            "
                                            >Offert
                                        </el-checkbox>
                                    </div>
                                    <div class="ml-4 flex-level aright">
                                        <el-switch
                                            :disabled="
                                                !canProductBeToggled(
                                                    getGroupedProductByCode(
                                                        optionalGroupOptions[index].plan,
                                                    ),
                                                    false,
                                                )
                                            "
                                            :modelValue="
                                                !!getCartItem(
                                                    getGroupedProductByCode(
                                                        optionalGroupOptions[index].plan,
                                                    ).id,
                                                )
                                            "
                                            @change="
                                                toggleItem(
                                                    getGroupedProductByCode(
                                                        optionalGroupOptions[index].plan,
                                                    ),
                                                )
                                            "
                                        />
                                    </div>
                                </template>
                            </div>
                        </li>
                    </ul>

                    <!-- TOTAL DISCOUNT -->
                    <div v-if="cartHasDiscount" class="mt-4">
                        <p class="my-1 wp-8 flex-level flex-item-right gray-60">
                            {{ $l10n.__('Sous total hors remises') }}
                            <span class="flex-right">
                                {{ $filters.numberFormat(currentPricingTotal.raw, true) }}
                            </span>
                        </p>
                        <p class="my-1 wp-8 flex-level flex-item-right gray-60">
                            {{ $l10n.__('Montant total remises') }}
                            <span class="flex-right">
                                {{ $filters.numberFormat(currentPricingTotal.discount, true) }}
                            </span>
                        </p>
                    </div>

                    <!-- MONTHLY CART SUM -->
                    <div class="my-4">
                        <h2
                            :class="{ 'primary-50': isCurrentPricingMonthly }"
                            class="wp-8 flex-level flex-item-right mt-3 mb-0"
                        >
                            {{ $filters.capitalize($l10n.__('Total mensuel')) }}
                            <span class="flex-right">
                                {{ $filters.numberFormat(cartTotal.monthly.totalDiscounted, true) }}
                            </span>
                        </h2>

                        <p
                            v-if="cartHasDiscount"
                            class="my-1 wp-8 flex-level flex-item-right gray-60"
                        >
                            {{ $l10n.__('Dont remise mensuelle') }}
                            <span class="flex-right">
                                {{ $filters.numberFormat(cartTotal.monthly.discount, true) }}
                            </span>
                        </p>

                        <p
                            v-if="
                                (!billingAddressTmp.vatIdentificationNumber ||
                                    !hasCountryIntraVat(billingAddressTmp.country)) &&
                                currentCountryTaxRate > 0
                            "
                            class="my-1 wp-8 flex-level flex-item-right gray-60"
                        >
                            {{ $l10n.__('TVA') }} ({{ currentCountryTaxRate * 100 }}%)
                            <span class="flex-right">
                                {{ $filters.numberFormat(cartTotal.monthly.tax, true) }}
                            </span>
                        </p>

                        <p
                            v-if="
                                (!billingAddressTmp.vatIdentificationNumber ||
                                    !hasCountryIntraVat(billingAddressTmp.country)) &&
                                currentCountryTaxRate > 0
                            "
                            class="my-1 wp-8 flex-level flex-item-right gray-60"
                        >
                            {{ $filters.capitalize($l10n.__('Total TTC mensuel')) }}
                            <span class="flex-right">
                                {{
                                    $filters.numberFormat(cartTotal.monthly.totalTaxIncluded, true)
                                }}
                            </span>
                        </p>
                    </div>

                    <!-- UPSALE - MONTHLY SUBSCRIBED PRODUCTS SUM -->
                    <div
                        v-if="isCartContext('upsale')"
                        class="my-2 px-1 pr-2 pb-1 default-bkg grey br"
                    >
                        <h2 class="wp-8 flex-level flex-item-right mt-2 pt-1 mb-0 fs-4">
                            {{ $filters.capitalize($l10n.__('total mensuel souscrit')) }}
                            <span class="flex-right">
                                <span
                                    v-if="newItemsOnSubscriptionTotal.monthly.totalTaxIncluded"
                                    :class="[
                                        newItemsOnSubscriptionTotal.monthly.totalTaxIncluded > 0
                                            ? 'blue'
                                            : 'red',
                                    ]"
                                    class="mr-1 fs-3 bold"
                                >
                                    ({{
                                        newItemsOnSubscriptionTotal.monthly.totalDiscounted > 0
                                            ? '+'
                                            : ''
                                    }}{{
                                        $filters.numberFormatWithOptions(
                                            newItemsOnSubscriptionTotal.monthly.totalDiscounted,
                                            { precision: 0 },
                                            true,
                                        )
                                    }})
                                </span>
                                {{
                                    $filters.numberFormat(
                                        customerSubscribedTotal.monthly.totalDiscounted,
                                        true,
                                    )
                                }}
                            </span>
                        </h2>

                        <p v-if="cartHasDiscount" class="my-1 wp-8 flex-level flex-item-right">
                            {{ $l10n.__('Dont remise mensuelle') }}
                            <span class="flex-right">
                                {{
                                    $filters.numberFormat(
                                        customerSubscribedTotal.monthly.discount,
                                        true,
                                    )
                                }}
                            </span>
                        </p>

                        <p
                            v-if="
                                (!billingAddressTmp.vatIdentificationNumber ||
                                    !hasCountryIntraVat(billingAddressTmp.country)) &&
                                currentCountryTaxRate > 0
                            "
                            class="my-1 wp-8 flex-level flex-item-right"
                        >
                            {{ $l10n.__('TVA') }} ({{ currentCountryTaxRate * 100 }}%)
                            <span class="flex-right">{{
                                $filters.numberFormat(customerSubscribedTotal.monthly.tax, true)
                            }}</span>
                        </p>

                        <p
                            v-if="
                                (!billingAddressTmp.vatIdentificationNumber ||
                                    !hasCountryIntraVat(billingAddressTmp.country)) &&
                                currentCountryTaxRate > 0
                            "
                            class="my-1 wp-8 flex-level flex-item-right"
                        >
                            {{ $filters.capitalize($l10n.__('Total TTC mensuel')) }}
                            <span class="flex-right">
                                <span
                                    v-if="newItemsOnSubscriptionTotal.monthly.totalTaxIncluded"
                                    :class="[
                                        newItemsOnSubscriptionTotal.monthly.totalTaxIncluded > 0
                                            ? 'blue'
                                            : 'red',
                                    ]"
                                    class="mr-1 fs-3 bold"
                                >
                                    ({{
                                        newItemsOnSubscriptionTotal.monthly.totalTaxIncluded > 0
                                            ? '+'
                                            : ''
                                    }}{{
                                        $filters.numberFormatWithOptions(
                                            newItemsOnSubscriptionTotal.monthly.totalTaxIncluded,
                                            { precision: 0 },
                                            true,
                                        )
                                    }})
                                </span>
                                {{
                                    $filters.numberFormat(
                                        customerSubscribedTotal.monthly.totalTaxIncluded,
                                        true,
                                    )
                                }}
                            </span>
                        </p>
                    </div>

                    <!-- YEARLY CART SUM -->
                    <div class="my-2 px-1 pr-2">
                        <h2
                            :class="{ 'primary-50': !isCurrentPricingMonthly }"
                            class="wp-8 flex-level flex-item-right mt-3 mb-0"
                        >
                            {{ $l10n.__('Total annuel') }}
                            <span class="flex-right">
                                {{ $filters.numberFormat(cartTotal.yearly.totalDiscounted, true) }}
                            </span>
                        </h2>

                        <p
                            v-if="cartHasDiscount"
                            class="my-1 wp-8 flex-level flex-item-right gray-60"
                        >
                            {{ $l10n.__('Dont remise annuelle') }}
                            <span class="flex-right">
                                {{ $filters.numberFormat(cartTotal.yearly.discount, true) }}
                            </span>
                        </p>

                        <p
                            v-if="
                                (!billingAddressTmp.vatIdentificationNumber ||
                                    !hasCountryIntraVat(billingAddressTmp.country)) &&
                                currentCountryTaxRate > 0
                            "
                            class="my-1 wp-8 flex-level flex-item-right gray-60"
                        >
                            {{ $l10n.__('TVA') }} ({{ currentCountryTaxRate * 100 }}%)
                            <span class="flex-right">
                                {{ $filters.numberFormat(cartTotal.yearly.tax, true) }}
                            </span>
                        </p>

                        <p
                            v-if="
                                (!billingAddressTmp.vatIdentificationNumber ||
                                    !hasCountryIntraVat(billingAddressTmp.country)) &&
                                currentCountryTaxRate > 0
                            "
                            class="my-1 wp-8 flex-level flex-item-right gray-60"
                        >
                            {{ $filters.capitalize($l10n.__('total TTC annuel')) }}
                            <span class="flex-right">
                                {{ $filters.numberFormat(cartTotal.yearly.totalTaxIncluded, true) }}
                            </span>
                        </p>
                    </div>

                    <!-- UPSALE - YEARLY SUBSCRIBED PRODUCTS SUM -->
                    <div
                        v-if="isCartContext('upsale')"
                        class="my-2 px-1 pr-2 pb-1 default-bkg grey br"
                    >
                        <h2 class="wp-8 flex-level flex-item-right mt-2 pt-1 mb-0 fs-4">
                            {{ $filters.capitalize($l10n.__('total annuel souscrit')) }}
                            <span class="flex-right">
                                <span
                                    v-if="newItemsOnSubscriptionTotal.yearly.totalTaxIncluded"
                                    :class="[
                                        newItemsOnSubscriptionTotal.yearly.totalTaxIncluded > 0
                                            ? 'blue'
                                            : 'red',
                                    ]"
                                    class="mr-1 fs-3 bold"
                                >
                                    ({{
                                        newItemsOnSubscriptionTotal.yearly.totalDiscounted > 0
                                            ? '+'
                                            : ''
                                    }}{{
                                        $filters.numberFormatWithOptions(
                                            newItemsOnSubscriptionTotal.yearly.totalDiscounted,
                                            { precision: 0 },
                                            true,
                                        )
                                    }})
                                </span>
                                {{
                                    $filters.numberFormat(
                                        customerSubscribedTotal.yearly.totalDiscounted,
                                        true,
                                    )
                                }}
                            </span>
                        </h2>

                        <p v-if="cartHasDiscount" class="my-1 wp-8 flex-level flex-item-right">
                            {{ $l10n.__('Dont remise annuelle') }}
                            <span class="flex-right">{{
                                $filters.numberFormat(customerSubscribedTotal.yearly.discount, true)
                            }}</span>
                        </p>

                        <p
                            v-if="
                                (!billingAddressTmp.vatIdentificationNumber ||
                                    !hasCountryIntraVat(billingAddressTmp.country)) &&
                                currentCountryTaxRate > 0
                            "
                            class="my-1 wp-8 flex-level flex-item-right"
                        >
                            {{ $l10n.__('TVA') }} ({{ currentCountryTaxRate * 100 }}%)
                            <span class="flex-right">{{
                                $filters.numberFormat(customerSubscribedTotal.yearly.tax, true)
                            }}</span>
                        </p>

                        <p
                            v-if="
                                (!billingAddressTmp.vatIdentificationNumber ||
                                    !hasCountryIntraVat(billingAddressTmp.country)) &&
                                currentCountryTaxRate > 0
                            "
                            class="my-1 wp-8 flex-level flex-item-right"
                        >
                            {{ $filters.capitalize($l10n.__('total TTC annuel')) }}
                            <span class="flex-right">
                                <span
                                    v-if="newItemsOnSubscriptionTotal.yearly.totalTaxIncluded"
                                    :class="[
                                        newItemsOnSubscriptionTotal.yearly.totalTaxIncluded > 0
                                            ? 'blue'
                                            : 'red',
                                    ]"
                                    class="mr-1 fs-3 bold"
                                >
                                    ({{
                                        newItemsOnSubscriptionTotal.yearly.totalTaxIncluded > 0
                                            ? '+'
                                            : ''
                                    }}{{
                                        $filters.numberFormatWithOptions(
                                            newItemsOnSubscriptionTotal.yearly.totalTaxIncluded,
                                            { precision: 0 },
                                            true,
                                        )
                                    }})
                                </span>
                                {{
                                    $filters.numberFormat(
                                        customerSubscribedTotal.yearly.totalTaxIncluded,
                                        true,
                                    )
                                }}
                            </span>
                        </p>
                    </div>

                    <!-- PAYMENT METHOD -->
                    <el-form-item
                        :error="errors.get('paymentMethod')"
                        class="bt-1 pt-3 m-0"
                        prop="paymentMethod"
                    >
                        <h4 class="mt-2 dinlineb">{{ $l10n.__('Moyen de paiement') }}</h4>
                        <RadioInput
                            :choices="paymentMethodsOptions"
                            :disabled="!isEditablePaymentMethod"
                            :modelValue="cartPaymentMethod"
                            class="dinlineb fright"
                            mode="edit"
                            radioStyle="button"
                            @change="paymentMethodUpdated"
                        />
                    </el-form-item>

                    <p v-if="cartEarlyRenewal" class="mt-3 wp-10 flex-row">
                        {{ $filters.capitalize($l10n.__('Restant à consommer')) }}
                        <span class="fs-3 flex-item-right">{{
                            formatAmount(-1 * centsToCurrency(cartEarlyRenewalDiscount), true)
                        }}</span>
                    </p>

                    <!-- PAYMENT FACILITY -->
                    <div v-if="isAvailablePaymentFacility" class="flex-level mt-3">
                        <h4 class="mt-2 flex">{{ $l10n.__('Facilité de paiement') }}</h4>
                        <SelectInput
                            :disabled="!isEditablePaymentFacility"
                            :mode="isEditablePaymentFacility ? 'edit' : 'view'"
                            :options="paymentFacilities"
                            :placeholder="
                                $t.__(
                                    'sellsyaccountmanager.subscriptionform.SubscriptionSale.select_payment_method',
                                )
                            "
                            :modelValue="cartPaymentFacility"
                            class="flex-right w-3"
                            idColumn="value"
                            labelColumn="label"
                            @update:modelValue="setCartPaymentFacility"
                        />
                    </div>

                    <template v-if="isCurrentPricingMonthly">
                        <p
                            v-for="deadline in deadlines"
                            :key="deadline.key"
                            class="my-1 mt-3 wp-10 flex-level flex-item-right gray-60"
                        >
                            {{ $filters.dateFormatter(deadline.date.unix(), 'NORMAL_DATE', false) }}
                            <span class="fs-3 flex-right">
                                {{ formatAmount(deadline.amount, true) }}
                            </span>
                        </p>

                        <p
                            v-if="deadlineUnchanged > 0 && deadlineUnchanged < 12"
                            class="my-1 wp-10 flex-level flex-item-right gray-60"
                        >
                            {{
                                $l10n.__('%(deadlineUnchanged)s échéances suivantes', {
                                    deadlineUnchanged: deadlineUnchanged,
                                })
                            }}
                            <span class="fs-3 flex-right">
                                {{ formatAmount(currentPricingTotal.totalTaxIncluded, true) }}
                            </span>
                        </p>
                    </template>
                    <template v-else>
                        <p
                            v-if="isPaymentFacilityEnabled"
                            class="my-1 mt-3 wp-10 flex-level flex-item-right"
                        >
                            {{
                                $filters.capitalize(
                                    $l10n.__('Paiement en %s fois', cartPaymentFacility),
                                )
                            }}
                            <span
                                v-if="cartNextAmountFacility < cartFirstAmountFacility"
                                class="fs-3 flex-right"
                            >
                                1 x {{ formatAmount(cartFirstAmountFacility, true) }}<br />
                            </span>
                            <span v-else class="fs-3 flex-right">
                                {{ cartPaymentFacility }} x
                                {{ formatAmount(cartFirstAmountFacility, true) }}
                            </span>
                        </p>
                        <p
                            v-if="
                                isPaymentFacilityEnabled &&
                                cartNextAmountFacility < cartFirstAmountFacility
                            "
                            class="my-1 wp-10 flex-level flex-item-right"
                        >
                            <span class="fs-3 flex-right">
                                {{ cartPaymentFacility - 1 }} x
                                {{ formatAmount(cartNextAmountFacility, true) }}
                            </span>
                        </p>
                        <p
                            v-if="isCustomPaymentFacilityEnabled"
                            class="my-1 wp-10 flex-level flex-item-right"
                        >
                            {{
                                $filters.capitalize(
                                    $l10n.__('Paiement en %s fois', cartNbCustomFacilities),
                                )
                            }}
                            <span
                                v-if="cartFirstAmountCustomFacility < cartLastAmountCustomFacility"
                                class="fs-3 flex-right"
                            >
                                {{ cartNbFirstCustomFacilities }} x
                                {{ formatAmount(cartFirstAmountCustomFacility, true) }}<br />
                            </span>
                            <span v-else class="fs-3 flex-right">
                                {{ cartNbCustomFacilities }} x
                                {{ formatAmount(cartFirstAmountFacility, true) }}
                            </span>
                        </p>
                        <p
                            v-if="
                                isCustomPaymentFacilityEnabled &&
                                cartFirstAmountCustomFacility < cartLastAmountCustomFacility
                            "
                            class="my-1 wp-6 flex-level flex-item-right"
                        >
                            <span class="fs-3 flex-right">
                                1 x {{ formatAmount(cartLastAmountCustomFacility, true) }}
                            </span>
                        </p>
                    </template>
                </el-form>

                <!-- LINK -->
                <div
                    v-if="currentCartUrlForCustomer && savedCartStatus !== 'draft'"
                    class="flex-level mt-5"
                >
                    <h4 class="m-0">{{ $l10n.__('Lien du panier :') }}</h4>
                    <TextInput
                        :readonly="true"
                        :modelValue="currentCartUrlForCustomer"
                        class="flex mx-3"
                        mode="edit"
                    />
                    <a
                        class="btn btn-default"
                        role="button"
                        @click="handleCopyToClipboardWithToast(currentCartUrlForCustomer)"
                    >
                        {{ $l10n.__('Copier') }}
                    </a>
                </div>
            </el-col>
        </el-row>

        <bottom-bar>
            <!-- BUTTONS -->
            <div>
                <a :href="urlToCustomerThirdOverview" class="btn btn-default mr-5" role="button">
                    {{ $l10n.__('Retour') }}
                </a>
                <a
                    v-if="isCartLoaded"
                    :disabled="!canCartBeCanceled"
                    class="btn btn-danger mr-2"
                    role="button"
                    @click.prevent="canCartBeCanceled ? confirmCartDeletion(cancelCart) : ''"
                >
                    {{ $l10n.__('Supprimer le panier') }}
                </a>
            </div>
            <div class="flex-right">
                <a
                    :disabled="!canCartBeSaved"
                    class="btn btn-secondary"
                    role="button"
                    @click.prevent="
                        canCartBeSaved ? submitForm('subscriptionForm', 'saveDraft') : ''
                    "
                >
                    {{
                        !isCartLoaded || (isCartLoaded && savedCartStatus == 'draft')
                            ? $l10n.__('Enregistrer en brouillon')
                            : $l10n.__('Enregistrer et repasser en brouillon')
                    }}
                </a>
                <a
                    :disabled="!canCartBeSaved"
                    class="btn btn-primary ml-5"
                    role="button"
                    @click.prevent="canCartBeSaved ? submitForm('subscriptionForm') : ''"
                >
                    {{
                        isCartLoaded && savedCartStatus !== 'draft'
                            ? $l10n.__('Enregistrer et renvoyer au client')
                            : $l10n.__('Enregistrer et envoyer au client')
                    }}
                </a>
            </div>
        </bottom-bar>
    </div>
</template>

<script>
import SAMMixins from '../../../mixins/subscription/SAMMixins';
import FormMixins from '../../../mixins/FormMixins';
import AlgoliaInput from '../../inputs/AlgoliaInput.vue';
import TelInput from '../../inputs/TelInput.vue';
import BottomBar from '../../utilities/BottomBar.vue';
import RadioInput from 'vcomponents/inputs/RadioInput.vue';
import SelectInput from 'vcomponents/inputs/SelectInput.vue';
import TextInput from 'vcomponents/inputs/TextInput.vue';
import DateInput from 'vcomponents/inputs/DateInput.vue';
import SellsyAccountManagerRoutes from 'routes/sellsyaccountmanager';
import RichText from 'vcomponents/richtext/RichText.vue';

import { createNamespacedHelpers } from 'vuex';
import { handleCopyToClipboardWithToast } from 'Helpers/stringHelper';

// NEW
import WidgetMixins from '../../../mixins/sellsyaccountmanager/widgetMixins';

import Feature from 'Services/Feature';

const userMapState = createNamespacedHelpers('user').mapState;

export default {
    components: {
        'algolia-places': AlgoliaInput,
        'tel-input': TelInput,
        'bottom-bar': BottomBar,
        RadioInput,
        SelectInput,
        TextInput,
        DateInput,
        RichText,
    },

    mixins: [SAMMixins, FormMixins, WidgetMixins],
    props: {
        permissions: {
            type: Object,
        },
    },
    data() {
        return {
            saleContextSelected: '',
            startDate: '',
            expireDate: '',
            activeCustomMail: false,
            billingCustomMail: {
                message: '',
                contactAdded: false,
                contactName: '',
                contactMailAddress: '',
                contactPhone: '',
            },
            billingCustomMailContactPhone: {
                defaultCC: 'fr',
                CC: '',
                dialCode: '',
                e164: '',
                e164Formated: '',
            },
            paymentFacilities: [
                {
                    value: 1,
                    label: this.$l10n.__('Une seule fois'),
                },
                {
                    value: 2,
                    label: this.$l10n.__('2 fois (à réception, à 30j.)'),
                },
                {
                    value: 3,
                    label: this.$l10n.__('3 fois (à réception, à 30j., à 60j.)'),
                },
                {
                    value: 10301,
                    label: this.$l10n.__(
                        '3 fois (1/12ème à réception, 1/12ème à 30j., solde à 60j.)',
                    ),
                },
            ],
            userPricingSteps: [
                {
                    up_to: 0,
                    unit_amount: 0,
                    nbLicences: 0,
                },
                {
                    up_to: 'inf',
                    unit_amount: 0,
                    nbLicences: 2,
                },
            ],
            minLicenceValidator: [
                { min: 2, message: this.$l10n.__('Veuillez saisir au minimum 2 licences') },
            ],
            offersProducts: [],
            optionalGroupOptions: [],
            optionsRichText: [
                'Heading',
                'Bold',
                'Italic',
                'BulletList',
                'OrderedList',
                'Divider',
                'Link',
            ],
        };
    },
    created() {
        // define nb minimum of licenses
        if (!this.isCartContext('renew') && this.customerSubscribedLicenses)
            this.tmpNbMinLicenses = this.customerSubscribedLicenses;

        // define nb of licenses
        if (this.cartNbLicenses) this.tmpNbLicenses = this.cartNbLicenses;
        else if (this.activeCustomerAccounts.length > this.tmpNbMinLicenses)
            this.tmpNbLicenses = this.activeCustomerAccounts.length;
        else this.tmpNbLicenses = this.tmpNbMinLicenses;

        this.initPricingStep();

        this.pricingPlanTmp = this.currentPricingPlan;

        this.initBillingContactTmp();
        this.initBillingAddressTmp();
        this.initCustomMail();

        // Period appliance start date
        if (this.customerServiceEndDate) {
            this.startDate = moment(this.customerServiceEndDate)
                .clone()
                .add(1, 'days')
                .format('YYYY-MM-DD HH:mm:ss');
            this.periodApplianceStartUpdated(moment(this.startDate).format());
        }

        if (this.isCartLoaded && this.cartPeriodStartDate)
            this.startDate = this.cartPeriodStartDateObj.format('YYYY-MM-DD HH:mm:ss');
        if (this.isCartLoaded && this.cartExpiredDate)
            this.expireDate = this.cartExpiredDateObj.format('YYYY-MM-DD HH:mm:ss');

        this.saleContextSelected = this.cartContext;

        if (!this.cartEarlyRenewalDiscount && this.isCartContext('renew')) {
            this.getCreditNote(this.customerCorporation).then((res) => {
                this.setCartEarlyRenewalDiscount(res);
            });
        }

        this.customerSubscribedTotal = this.buildTotal(
            this.subscribedProductsIterable,
            this.billingSettings.pricingPlanCode || this.currentPricingPlan,
        );
        this.optionalGroupOptions = this.pricingOptionalProductsGroups.map((group) => {
            // Get saved group code in cart or get first group item code by default
            const defaultGroup =
                this.pricingOptionalProductsGrouped.find((item) =>
                    this.savedCartItems.find((cartItem) => cartItem.product_id === item.id),
                )?.code ??
                this.pricingOptionalProductsGrouped.find((item) => item.groups.includes(group))
                    .code;
            return {
                id: group,
                plan: defaultGroup,
            };
        });
    },
    computed: {
        ...userMapState(['forename', 'name', 'email', 'phoneNumber']),
        canEarlyRenew() {
            return (
                this.billingSettings.mrr > 0 &&
                (this.saleContextSelected === 'legacy_renew' || this.isCartContext('renew'))
            );
        },
        saleContexts() {
            return [
                {
                    value: 'new',
                    label: 'New plan',
                    disabled: this.customer.levelReadable === 'gold',
                },
                {
                    value: this.billingSettings.pricingVersion < 3 ? 'legacy_renew' : 'renew',
                    label: 'Renew',
                    disabled:
                        this.customer.levelReadable !== 'gold' ||
                        (this.billingSettings.pricingVersion < 3
                            ? !Feature.isEnabled('sam-cart-availability-for-legacy-renew')
                            : !Feature.isEnabled('sam-cart-availability-for-renew')),
                },
                {
                    value: 'upsale',
                    label: 'Upsell',
                    disabled:
                        this.customer.levelReadable === 'trial' ||
                        this.billingSettings.pricingVersion < 3,
                },
            ];
        },
        pricingPlansChoices() {
            return [
                {
                    value: 'MONTHLY',
                    label: this.$l10n.__('Mensuel'),
                    disabled: this.cartPaymentMethod === 'manual',
                },
                {
                    value: 'YEARLY',
                    label: this.$l10n.__('Annuel'),
                },
            ];
        },
        currentCartUrlForCustomer() {
            const route = SellsyAccountManagerRoutes.find(
                (route) => route.name == 'sam.selfservice.cart.form',
            );
            const url = this.buildRoute(route, [], true);
            if (null == url) return;
            if (this.isCartLoaded) return url + '?id=' + this.cartId;
            else return url + '?context=' + this.cartContext;
        },
        totalNbLicences: {
            get: function () {
                return this.userPricingSteps
                    .map((step) => step.nbLicences)
                    .reduce((acc, next) => acc + next, 0);
            },
            set: function () {
                return null;
            },
        },
    },
    methods: {
        handleCopyToClipboardWithToast,
        handleCustomMail(e) {
            if (!e) this.billingCustomMail.message = '';
        },
        getMininalLicence(currentNbLicences, defaultMin) {
            if (this.totalNbLicences === 2) {
                if (currentNbLicences === this.totalNbLicences) {
                    return 2;
                } else {
                    return currentNbLicences;
                }
            } else {
                return defaultMin;
            }
        },
        setEarly(e) {
            this.setCartEarlyRenewal(e.target.checked);
        },
        updateCustomMailContactPhone(phone) {
            this.billingCustomMail.contactPhone = phone.e164;
        },
        initPricingStep() {
            if (this.isCartLoaded) {
                if (_.isNil(this.savedCartItems[0].customPricing)) {
                    const freeStep = this.userPricingSteps.find(
                        (pricing) => pricing.unit_amount === 0,
                    );
                    const infStep = this.userPricingSteps.find(
                        (pricing) => pricing.up_to === 'inf',
                    );

                    freeStep.nbLicences = this.savedCartItems[0].quantityFree;
                    infStep.nbLicences = this.savedCartItems[0].quantity;
                    infStep.unit_amount = this.getProductAmount(
                        this.pricingBasicProduct,
                        this.currentPricingPlan,
                    );

                    this.updateNbFreeLicences(freeStep.up_to);
                    this.updatePricingStep();
                    return;
                }
                const hasFreeLicences = !!this.savedCartItems[0].customPricing.find(
                    (pricing) => pricing.unit_amount === 0,
                );
                if (!hasFreeLicences) {
                    this.savedCartItems[0].customPricing.unshift({
                        up_to: 0,
                        unit_amount: 0,
                        nbLicences: 0,
                    });
                }

                this.userPricingSteps = this.savedCartItems[0].customPricing.map((step, index) => {
                    let nbLicences;

                    if (index === 0) nbLicences = step.up_to;
                    else if (index === this.savedCartItems[0].customPricing.length - 1)
                        nbLicences =
                            this.savedCartItems[0].quantity -
                            this.savedCartItems[0].customPricing[index - 1].up_to;
                    else
                        nbLicences =
                            step.up_to - this.savedCartItems[0].customPricing[index - 1].up_to;

                    return {
                        ...step,
                        nbLicences,
                    };
                });

                this.updateNbFreeLicences(
                    this.userPricingSteps.find((pricing) => pricing.unit_amount === 0)?.up_to,
                );
                this.updatePricingStep();
            } else if (!_.isNil(this.savedCartItems[0]?.customPricing)) {
                const hasFreeLicences = !!this.savedCartItems[0].customPricing.find(
                    (pricing) => pricing.unit_amount === 0,
                );
                if (!hasFreeLicences) {
                    this.savedCartItems[0].customPricing.unshift({
                        up_to: 0,
                        unit_amount: 0,
                        nbLicences: 0,
                    });
                }
                this.userPricingSteps = this.savedCartItems[0].customPricing;
                this.updateNbFreeLicences(
                    this.userPricingSteps.find((pricing) => pricing.unit_amount === 0)?.up_to,
                );
                this.updatePricingStep();
            } else {
                const initialPricingStep = this.userPricingSteps.find(
                    (pricing) => pricing.up_to === 'inf',
                );
                initialPricingStep.nbLicences = this.tmpNbLicenses;
                initialPricingStep.unit_amount = this.getProductMonthlyAmount(
                    this.pricingBasicProduct,
                    this.currentPricingPlan,
                    false,
                );
                this.updatePricingStep();
                this.updateNbFreeLicences(0);
            }
        },
        initCustomMail() {
            this.billingCustomMail = {
                ...this.billingCustomMail,
                contactName: `${this.forename} ${this.name}`,
                contactMailAddress: this.email,
                contactPhone: this.phoneNumber,
            };

            if (this.isCartLoaded && this.cartContactAdded) {
                this.billingCustomMail = {
                    ...this.billingCustomMail,
                    contactAdded: this.cartContactAdded,
                    contactName: this.cartContactName,
                    contactMailAddress: this.cartContactMailAddress,
                    contactPhone: this.cartContactPhone,
                };
            }

            if (this.billingCustomMail.contactPhone[0] == '+')
                this.billingCustomMailContactPhone.e164 = this.billingCustomMail.contactPhone;
            else this.billingCustomMailContactPhone.dialCode = this.billingCustomMail.contactPhone;
        },

        updateLicences(nbLicence, index) {
            const step = this.userPricingSteps[index];
            step.nbLicences = nbLicence;
            if (step.up_to !== 'inf') {
                step.up_to =
                    index === 0
                        ? nbLicence
                        : this.userPricingSteps[index - 1].up_to + step.nbLicences;
            }
            this.userPricingSteps[index] = step;
            if (index !== this.userPricingSteps.length - 1) {
                this.updateLicences(this.userPricingSteps[index + 1].nbLicences, index + 1);
                if (index === 0) {
                    this.updateNbFreeLicences(nbLicence);
                }
            } else {
                this.updatePricingStep();
            }
        },
        updatePricingStep() {
            this.updateUsersLicencesCustomPricing(
                this.pricingBasicProduct.id,
                this.userPricingSteps,
            );
        },
        addPricingStep() {
            const previousLastPricing = this.userPricingSteps[this.userPricingSteps.length - 1];
            previousLastPricing.up_to =
                this.userPricingSteps[this.userPricingSteps.length - 2].up_to +
                previousLastPricing.nbLicences;
            this.userPricingSteps.push({
                up_to: 'inf',
                unit_amount: this.getProductMonthlyAmount(
                    this.pricingBasicProduct,
                    this.currentPricingPlan,
                    false,
                ),
                nbLicences: 1,
            });
        },

        /**
         * Switch steps pricing by other plan's catalog price or keep custom pricing
         */
        switchAllStepsPricing(plan) {
            if (this.currentPricingPlan === plan) {
                return;
            }

            this.userPricingSteps = this.userPricingSteps.map((pricing, index) => {
                if (index === 0) return pricing;
                let amountUpdated = 0;

                if (
                    this.getProductPlan(this.pricingBasicProduct, this.currentPricingPlan)
                        .amountInCents === pricing.unit_amount
                ) {
                    amountUpdated = this.getProductPlan(
                        this.pricingBasicProduct,
                        plan,
                    ).amountInCents;
                } else {
                    const newPlanPricingAmount =
                        plan === 'MONTHLY' ? pricing.unit_amount / 12 : pricing.unit_amount * 12;
                    const equivalentPricingInAnotherPlan = this.getCustomPricingAmountForProduct(
                        this.pricingBasicProduct,
                        plan,
                    ).find((amount) => amount.value === newPlanPricingAmount);
                    amountUpdated = equivalentPricingInAnotherPlan
                        ? equivalentPricingInAnotherPlan.value
                        : _.last(
                            this.getCustomPricingAmountForProduct(this.pricingBasicProduct, plan),
                        ).value;
                }

                return {
                    ...pricing,
                    unit_amount: amountUpdated,
                };
            });
            this.updatePricingStep();
        },

        deletePricingStep(index) {
            this.userPricingSteps.splice(index, 1);
            this.processPricingStep(index - 1);
        },

        processPricingStep(index) {
            if (index === this.userPricingSteps.length - 1) {
                this.userPricingSteps[index].up_to = 'inf';
                return;
            }

            const previousStep = this.userPricingSteps[index - 1];
            let currentStep = this.userPricingSteps[index];
            currentStep = {
                ...currentStep,
                up_to:
                    currentStep.up_to !== 'inf'
                        ? previousStep.up_to + currentStep.nbLicences
                        : 'inf',
            };
            this.userPricingSteps[index] = currentStep;
            this.processPricingStep(index + 1);
        },

        // Global form record
        saveDraft() {
            return this.save(true);
        },
        save(asDraft) {
            // Print loader
            this.$emit('load', true);

            // Check if form is valid
            return this.isValid()
                .then(() => {
                    // BillingAddress
                    // - record is needed ?
                    if (!this.billingAddressHasChanged) return;
                    // - record
                    return this.recordBillingAddress(
                        this.customerCorporation,
                        this.billingAddressTmp,
                    ).then((address) => this.setBillingAddress(address));
                })
                .then(() => {
                    // BillingContact
                    // - record is needed ?
                    if (!this.billingContactHasChanged) return;
                    // - record
                    return this.recordBillingContact(
                        this.customerCorporation,
                        this.billingContactTmp,
                        this.billingContactPhoneTmp,
                    ).then((contact) => {
                        this.setBillingContact(contact);
                    });
                })
                .then(() => {
                    // BillingSettings
                    // - record is needed ?
                    if (!this.billingSettingsHasChanged) return;
                    // - record
                    return this.recordBillingSettings(
                        this.customerCorporation,
                        _.merge(this.billingSettings, {
                            vatIdentificationNumber: this.billingAddressTmp.vatIdentificationNumber,
                        }),
                    ).then((settings) => this.setBillingSettings(settings));
                })
                .then(() => {
                    // Cart
                    // - record
                    const callback = (cart) => {
                        Push.success(this.$l10n.__('Panier créé'));
                        this.loadFromCart(cart);
                    };
                    EventBus.emit('saveCustomMail', this.billingCustomMail);
                    return !!asDraft ? this.saveDraftCart(callback) : this.saveCart(callback);
                })
                .catch((e) => {
                    switch (e.message) {
                        case 'billingcontact':
                            Push.error(
                                this.$l10n.__('Certains champs sont invalides ou incomplets'),
                                this.$l10n.__('Contact de facturation'),
                            );
                            break;
                        case 'billingaddress':
                            Push.error(
                                this.$l10n.__('Certains champs sont invalides ou incomplets'),
                                this.$l10n.__('Adresse de facturation'),
                            );
                            break;
                        case 'billingCustomMail':
                            Push.error(
                                this.$l10n.__('Certains champs sont invalides ou incomplets'),
                                this.$l10n.__('Personnalisation des emails'),
                            );
                            break;
                        case 'minimalLicences':
                            Push.error(
                                this.$l10n.__("Le nombre d'utilisateur doit être au minimum de 2."),
                                this.$l10n.__('Panier Client'),
                            );
                            break;
                        default:
                            Push.error(this.$l10n.__('Une erreur est survenue'));
                            break;
                    }
                    this.$emit('load', false);
                });
        },
        cancelCart() {
            return window.EventBus.emit('cancelcart', () => {
                Push.success(this.$l10n.__('Panier annulé'));
                window.location.href = this.urlCartSale;
            });
        },
        isValid() {
            return new Promise((resolve, reject) => {
                if (!this.isBillingContactValid) {
                    throw new Error('billingcontact');
                } else if (!this.isBillingAddressValid) {
                    throw new Error('billingaddress');
                } else if (!this.isBillingCustomMailValid) {
                    throw new Error('billingCustomMail');
                } else if (!this.arePricingProductsCompliantAsCartItems()) {
                    throw new Error();
                } else if (this.totalNbLicences < 2) {
                    throw new Error('minimalLicences');
                }
                resolve(true);
            });
        },

        // Store data update
        paymentMethodUpdated(paymentMethod) {
            // default value
            if (this.paymentMethodsOptions.map((c) => c.value).indexOf(paymentMethod) === -1)
                paymentMethod = 'stripe_card';

            // force pricing plan to yearly if payment method is manual
            if (this.isCurrentPricingMonthly && paymentMethod === 'manual') {
                this.pricingPlanTmp = 'YEARLY';
                this.switchPricingPlan(this.pricingPlanTmp);
            }

            return this.setCartPaymentMethod(paymentMethod);
        },
        contextUpdated(context) {
            this.setCartContext(context);
            window.EventBus.emit('changecartContext', context);
        },
        periodApplianceStartUpdated(date) {
            return this.setCartPeriodStart(date);
        },
        expirationDateUpdated(date) {
            return this.setCartExpiration(date);
        },

        offerProduct(product, index, isChecked) {
            if (isChecked) {
                if (!this.getCartItem(product.id)) {
                    this.toggleItem(product);
                }

                this.offersProducts.push(index);
                this.updateCartItemCustomPricing(product.id, 0);
            } else {
                this.offersProducts = this.offersProducts.filter((product) => product !== index);
                this.updateCartItemCustomPricing(product.id, null);
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.rangeInputDate {
    .el-form-item__content {
        line-height: 0;
    }
}

.step-subcription-root {
    padding-bottom: 90px;
}

.product-list .product-line-title {
    width: 120px;
}

.product-list .product-line-price > div,
.amount-month-price-select {
    width: 70px;
}

.pricing-step {
    transition: all 0.3s ease;

    &:hover {
        .delete-pricing-step {
            opacity: 1;
            right: 0;
        }
    }
}

.delete-pricing-step {
    background: linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 1) 25%);
    border: none;
    opacity: 0;
    position: absolute;
    right: -10px;
    transition: all 0.3s ease;
}
</style>

<style lang="scss">
.step-subcription-root {
    .el-input-number.is-controls-right {
        input {
            height: 29px;
        }
    }

    .total-user {
        input {
            text-align: right;
        }
    }
}

.no-caret .el-select__caret {
    display: none;
}
</style>
